import { z } from 'zod';

/**
 * What an edge can actually do, reported by the edge and enforced by the
 * controller.
 *
 * The controller must never assume every platform implements the same things. A
 * pfSense node and a Debian appliance both terminate WireGuard, but one has pf
 * and the other nftables, one may lack BFD, and their route capacities differ by
 * an order of magnitude. Sending configuration a node cannot apply produces a
 * deployment that reports success and a device that quietly does not do what was
 * asked.
 *
 * The document is signed by the device key rather than trusted from the
 * transport alone. The edge channel accepts a client certificate asserted by a
 * trusted TLS-terminating proxy (`EDGE_CERT_HEADER`), so with a proxy in front
 * the transport identity is only as good as the proxy. A signature over the
 * document binds it to the device's own key either way.
 */

export const CAPABILITY_DOCUMENT_VERSION = 1;

/** Platform families the controller can reason about. */
export const PLATFORM_FAMILIES = [
  'CONNARO_APPLIANCE',
  'LINUX',
  'PFSENSE',
  'OPNSENSE',
  'OPENWRT',
  'VYOS',
  'OTHER',
] as const;
export type PlatformFamily = (typeof PLATFORM_FAMILIES)[number];

export const FIREWALL_IMPLEMENTATIONS = [
  'NFTABLES',
  'IPTABLES',
  'PF',
  'FIREWALL4',
  'NONE',
] as const;
export type FirewallImplementation = (typeof FIREWALL_IMPLEMENTATIONS)[number];

export const ROUTING_IMPLEMENTATIONS = ['FRR', 'BIRD', 'NATIVE', 'NONE'] as const;
export type RoutingImplementation = (typeof ROUTING_IMPLEMENTATIONS)[number];

/**
 * Individually negotiable features.
 *
 * Each maps to configuration the controller might otherwise send. A feature
 * absent here is not a missing field to default — it is a thing the node cannot
 * do, and the compiler must leave it out rather than emit it and hope.
 */
export const CAPABILITY_FEATURES = [
  'wireguard',
  'ospfv2',
  'ospfv3',
  'bgp',
  'bfd',
  'policyRouting',
  'vrf',
  'multiWan',
  'qos',
  'transactionalCommit',
  'rollback',
  'localRecovery',
  'update',
] as const;
export type CapabilityFeature = (typeof CAPABILITY_FEATURES)[number];

const featureSchema = z.object(
  Object.fromEntries(CAPABILITY_FEATURES.map((f) => [f, z.boolean().default(false)])) as Record<
    CapabilityFeature,
    z.ZodDefault<z.ZodBoolean>
  >,
);

/**
 * Capacity ceilings.
 *
 * These are the node's own statement of what it can carry, not a controller
 * guess. A small ARM appliance and a rack server run the same software with very
 * different limits, and exceeding them degrades a site rather than failing
 * loudly, so the controller warns before deploying rather than after.
 */
export const capabilityLimitsSchema = z.object({
  maxPeers: z.number().int().positive(),
  maxTunnels: z.number().int().positive(),
  maxRoutes: z.number().int().positive(),
  maxPolicies: z.number().int().positive(),
});
export type CapabilityLimits = z.infer<typeof capabilityLimitsSchema>;

export const capabilityDocumentSchema = z.object({
  documentVersion: z.number().int().positive(),
  platformFamily: z.enum(PLATFORM_FAMILIES),
  platformVersion: z.string().min(1).max(64),
  architecture: z.string().min(1).max(32),
  edgePackageVersion: z.string().min(1).max(64),
  protocolVersion: z.number().int().positive(),
  features: featureSchema,
  firewall: z.enum(FIREWALL_IMPLEMENTATIONS),
  routing: z.object({
    implementation: z.enum(ROUTING_IMPLEMENTATIONS),
    version: z.string().max(64).optional(),
  }),
  limits: capabilityLimitsSchema,
  /** Features the node knows about but has deliberately disabled locally. */
  disabledFeatures: z.array(z.enum(CAPABILITY_FEATURES)).default([]),
});
export type CapabilityDocument = z.infer<typeof capabilityDocumentSchema>;

/** Whether a feature is usable: supported by the platform and not disabled locally. */
export function supports(doc: CapabilityDocument, feature: CapabilityFeature): boolean {
  if (doc.disabledFeatures.includes(feature)) return false;
  return doc.features[feature];
}

export interface PlatformProfile {
  family: PlatformFamily;
  displayName: string;
  /**
   * Whether Connaro ships a runtime that manages this platform. A platform that
   * is understood but unmanaged can be inventoried and reasoned about; it cannot
   * be deployed to, and saying so plainly is better than a provider that appears
   * to work.
   */
  managed: boolean;
  expectedFirewall: FirewallImplementation;
  expectedRouting: RoutingImplementation;
  /** Features this platform is expected to offer, for contradiction checks. */
  expectedFeatures: Partial<Record<CapabilityFeature, boolean>>;
  notes: string;
}

/**
 * What each platform is expected to look like.
 *
 * These are not a substitute for a node's own report — a node always reports its
 * own capabilities, and the report wins. The profile exists to notice a
 * contradiction, which usually means a misconfigured or misrepresenting node.
 */
export const PLATFORM_PROFILES: Record<PlatformFamily, PlatformProfile> = {
  CONNARO_APPLIANCE: {
    family: 'CONNARO_APPLIANCE',
    displayName: 'Connaro Appliance',
    managed: true,
    expectedFirewall: 'NFTABLES',
    expectedRouting: 'FRR',
    expectedFeatures: {
      wireguard: true,
      ospfv2: true,
      ospfv3: true,
      bgp: true,
      transactionalCommit: true,
      rollback: true,
      localRecovery: true,
      update: true,
    },
    notes: 'Owns its full stack, so every managed feature is expected.',
  },
  LINUX: {
    family: 'LINUX',
    displayName: 'Generic Linux',
    managed: true,
    expectedFirewall: 'NFTABLES',
    expectedRouting: 'FRR',
    expectedFeatures: { wireguard: true, transactionalCommit: true, rollback: true },
    notes:
      'Installed alongside an existing system, so routing and firewall availability ' +
      'depend on what the host already runs.',
  },
  PFSENSE: {
    family: 'PFSENSE',
    displayName: 'pfSense / Netgate',
    managed: false,
    expectedFirewall: 'PF',
    expectedRouting: 'FRR',
    expectedFeatures: { wireguard: true, multiWan: true },
    notes:
      "The console renders this platform's firewall policy to pf (renderPfAnchor), " +
      'so intent can be compiled for it; the on-box runtime that applies that pf, ' +
      'commits transactionally, and rolls back does not ship yet, so the platform ' +
      'stays unmanaged and support is not claimed for any pfSense release until a ' +
      'runtime has passed the provider contract.',
  },
  OPNSENSE: {
    family: 'OPNSENSE',
    displayName: 'OPNsense',
    managed: false,
    expectedFirewall: 'PF',
    expectedRouting: 'FRR',
    expectedFeatures: { wireguard: true, multiWan: true },
    notes:
      'Shares the pf firewall renderer with pfSense (renderPfAnchor); as with pfSense, ' +
      'no on-box runtime ships yet, so it stays unmanaged. Parity with pfSense is not assumed.',
  },
  OPENWRT: {
    family: 'OPENWRT',
    displayName: 'OpenWrt',
    managed: false,
    expectedFirewall: 'FIREWALL4',
    expectedRouting: 'NATIVE',
    expectedFeatures: { wireguard: true },
    notes:
      'Package architecture is defined but no runtime ships yet. Capacity is hardware-constrained.',
  },
  VYOS: {
    family: 'VYOS',
    displayName: 'VyOS',
    managed: false,
    expectedFirewall: 'NFTABLES',
    expectedRouting: 'FRR',
    expectedFeatures: { wireguard: true, bgp: true, ospfv2: true },
    notes: 'Capability-based integration is planned; no runtime ships yet.',
  },
  OTHER: {
    family: 'OTHER',
    displayName: 'Other',
    managed: false,
    expectedFirewall: 'NONE',
    expectedRouting: 'NONE',
    expectedFeatures: {},
    notes: 'Inventoried only. Nothing is deployed to an unrecognised platform.',
  },
};

/** True when Connaro ships a runtime that can manage this platform. */
export function isManagedPlatform(family: PlatformFamily): boolean {
  return PLATFORM_PROFILES[family].managed;
}

export interface CapabilityReview {
  errors: string[];
  warnings: string[];
}

/**
 * Check a reported document for internal contradictions and for disagreement
 * with what the platform should look like.
 *
 * A node's own report is authoritative; these are the cases where believing it
 * verbatim would produce configuration that cannot work.
 */
export function reviewCapabilities(doc: CapabilityDocument): CapabilityReview {
  const errors: string[] = [];
  const warnings: string[] = [];
  const profile = PLATFORM_PROFILES[doc.platformFamily];

  if (doc.documentVersion > CAPABILITY_DOCUMENT_VERSION) {
    // A newer document may carry fields this controller does not understand, so
    // it is refused rather than partially interpreted.
    errors.push(
      `Capability document version ${doc.documentVersion} is newer than this controller ` +
        `understands (${CAPABILITY_DOCUMENT_VERSION}). Upgrade the console.`,
    );
  }

  // WireGuard is how an edge joins a fabric at all. A node without it can be
  // inventoried but cannot carry overlay traffic.
  if (!doc.features.wireguard) {
    warnings.push('This node reports no WireGuard support and cannot join a fabric.');
  }

  // Routing features need something to run them.
  if (doc.routing.implementation === 'NONE') {
    for (const feature of ['ospfv2', 'ospfv3', 'bgp'] as const) {
      if (doc.features[feature]) {
        errors.push(`Node reports ${feature} support but no routing implementation to provide it.`);
      }
    }
  }
  if (doc.features.bfd && !doc.features.ospfv2 && !doc.features.bgp) {
    warnings.push('BFD is reported without OSPF or BGP; nothing would consume it.');
  }
  if (doc.firewall === 'NONE' && doc.features.policyRouting) {
    warnings.push('Policy routing is reported without a firewall implementation to mark traffic.');
  }

  // Rollback without transactional commit means a failed apply cannot be undone
  // atomically, which changes the safe-apply strategy rather than being an error.
  if (doc.features.rollback && !doc.features.transactionalCommit) {
    warnings.push(
      'Rollback is reported without transactional commit; applies will use a ' +
        'staged strategy rather than an atomic one.',
    );
  }

  for (const [feature, expected] of Object.entries(profile.expectedFeatures) as Array<
    [CapabilityFeature, boolean]
  >) {
    if (expected && !doc.features[feature]) {
      warnings.push(
        `${profile.displayName} normally supports ${feature}, but this node reports it as ` +
          'unavailable.',
      );
    }
  }

  if (doc.firewall !== profile.expectedFirewall && profile.expectedFirewall !== 'NONE') {
    warnings.push(
      `${profile.displayName} normally uses ${profile.expectedFirewall}; this node reports ` +
        `${doc.firewall}.`,
    );
  }

  for (const feature of doc.disabledFeatures) {
    if (!doc.features[feature]) {
      warnings.push(`${feature} is listed as disabled but was not reported as supported.`);
    }
  }

  return { errors, warnings };
}

export interface CapacityDemand {
  peers?: number;
  tunnels?: number;
  routes?: number;
  policies?: number;
}

export interface CapacityFinding {
  resource: keyof CapabilityLimits;
  demand: number;
  limit: number;
  severity: 'warning' | 'error';
  message: string;
}

/** Fraction of a limit at which the console starts warning. */
export const CAPACITY_WARN_RATIO = 0.8;

/**
 * Compare planned demand against a node's declared ceilings.
 *
 * Exceeding a ceiling is an error because the node has said it cannot carry the
 * load; approaching one is a warning because the next site added will.
 */
export function checkCapacity(doc: CapabilityDocument, demand: CapacityDemand): CapacityFinding[] {
  const findings: CapacityFinding[] = [];
  const pairs: Array<[keyof CapabilityLimits, number | undefined, string]> = [
    ['maxPeers', demand.peers, 'peers'],
    ['maxTunnels', demand.tunnels, 'tunnels'],
    ['maxRoutes', demand.routes, 'routes'],
    ['maxPolicies', demand.policies, 'policies'],
  ];

  for (const [key, value, label] of pairs) {
    if (value === undefined) continue;
    const limit = doc.limits[key];
    if (value > limit) {
      findings.push({
        resource: key,
        demand: value,
        limit,
        severity: 'error',
        message: `This plan needs ${value} ${label} but the node supports ${limit}.`,
      });
    } else if (value >= Math.floor(limit * CAPACITY_WARN_RATIO)) {
      findings.push({
        resource: key,
        demand: value,
        limit,
        severity: 'warning',
        message: `This plan uses ${value} of ${limit} ${label} on this node.`,
      });
    }
  }
  return findings;
}

/**
 * Canonical bytes for signing and verification.
 *
 * Both ends must agree exactly, so keys are emitted in sorted order and the
 * result carries no incidental whitespace. Signing a JSON blob whose key order
 * depends on the producer would verify on one runtime and fail on another.
 *
 * This deliberately accepts the *transmitted* document rather than a parsed one.
 * Parsing fills in defaults for every unreported feature, so canonicalizing after
 * parsing produces different bytes from the ones the agent signed, and every
 * signature would fail to verify. A signature covers what was sent.
 */
export function canonicalCapabilityBytes(transmitted: unknown): string {
  return canonicalize(transmitted);
}

function canonicalize(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonicalize).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalize(v)}`).join(',')}}`;
}
